import type { RefObject } from 'react';
import { useEffect, useState, useRef } from 'react';
import { animated, useSpring } from 'react-spring';
import { styled } from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { GroupHeader } from '@hivebrite/design-system/GroupHeader';
import { useMatchBreakpoint, zIndices } from '@hivebrite/design-system/Theme';

import SidePreview from 'hb-react/frontoffice/components/SidePreview';
import { useUIContext } from 'hb-react/frontoffice/contexts/UIContext';
import MenuScrollable from 'hb-react/shared/apps/Topics/components/TopicHeaderV3/MenuScrollable';
import { useSearchTopic } from 'hb-react/shared/apps/Topics/containers/TopicShowContainer/hooks/useSearchTopic';
import { useSidePreview } from 'hb-react/shared/apps/Topics/containers/TopicShowContainer/hooks/useSidePreview';
import { useVerticalScroll } from 'hb-react/shared/apps/Topics/containers/TopicShowContainer/hooks/useVerticalScroll';
import type { TopicHeaderProps } from 'hb-react/shared/apps/Topics/containers/TopicShowContainer/types';
import { getGroupHeaderButtons } from 'hb-react/shared/apps/Topics/containers/TopicShowContainer/utils/getGroupHeaderButtons';
import UsersModal from 'hb-react/shared/components/DesignSystem/UsersModal';
import useFeatureFlag from 'hb-react/shared/hooks/useFeatureFlag';
import { ImmersiveMenu } from 'hb-react/shared/hooks/useImmersiveMenu';

interface UIContextType {
  appHeaderNavRef: RefObject<HTMLDivElement>;
  appSubHeaderRef: RefObject<HTMLDivElement>;
  setIsNetworkNavVisible: (show: boolean) => void;
}

export const TopicHeaderDesktop = ({
  currentNetwork,
  currentUser,
  following,
  members,
  socials,
  topic,
  topicsActions,
  totalMembers,
  unfollowTopic,
  updateSettings,
}: TopicHeaderProps) => {
  const isPO = !currentUser;
  const isSmallBreakpoint: boolean = useMatchBreakpoint(['s', 'm']);
  const { appHeaderNavRef, appSubHeaderRef, setIsNetworkNavVisible } =
    useUIContext() as unknown as UIContextType;
  const { handleSearch } = useSearchTopic({ topic });
  const { JoinButton, threeDotsButtonData } = getGroupHeaderButtons({
    following,
    isPO,
    unfollowTopic,
    updateSettings,
    topic,
  });
  const { isSidePreviewOpen, handleManageGroupClick, handleSidePreviewClose } =
    useSidePreview(topicsActions, topic);
  const { currentScrollY, isScrollingUp, isScrollingDown } =
    useVerticalScroll();
  const { flagUeSearchDeactivateGlobal } = useFeatureFlag();

  const mainGroupHeaderRef = useRef<HTMLDivElement>(null);

  const [isMembersModalOpen, setIsMembersModalOpen] = useState(false);
  const [networkHeaderHeight, setNetworkHeaderHeight] = useState(0);
  const [networkNavHeight, setNetworkNavHeight] = useState(0);
  const [isCollapsedHeader, setIsCollapsedHeader] = useState(false);
  const [
    isMenuVisibleWithCollapsedHeader,
    setIsMenuVisibleWithCollapsedHeader,
  ] = useState(false);

  const collapsedHeaderSpring = useSpring({
    // Display none to avoid been accessible by screen readers or keyboard
    from: { opacity: 0, transform: 'translateY(-100%)', display: 'none' },
    to: {
      opacity: isCollapsedHeader ? 1 : 0,
      transform: isCollapsedHeader ? 'translateY(0)' : 'translateY(-100%)',
      display: isCollapsedHeader ? 'block' : 'none',
    },
  });

  const menuSpring = useSpring({
    from: { opacity: 0, transform: 'translateY(-100%)', display: 'none' },
    to: {
      opacity: isMenuVisibleWithCollapsedHeader ? 1 : 0,
      transform: isMenuVisibleWithCollapsedHeader
        ? 'translateY(0)'
        : 'translateY(-100%)',
      // to avoid blank space under the title when scroll down
      display: isMenuVisibleWithCollapsedHeader ? 'block' : 'none',
    },
  });

  const calculateMainGroupHeaderHeight = () =>
    mainGroupHeaderRef.current
      ? mainGroupHeaderRef.current.offsetHeight +
        networkNavHeight +
        networkHeaderHeight
      : 0;

  const isMainGroupHeaderVisible = (
    mainGroupHeaderHeight: number,
    scrollY: number,
  ) => {
    const adjustedScrollY = scrollY + networkHeaderHeight;

    return adjustedScrollY < mainGroupHeaderHeight;
  };

  const updateGroupHeaderState = (isHeaderVisible: boolean) => {
    if (!isHeaderVisible && isScrollingDown) {
      setIsNetworkNavVisible(false);
      setIsCollapsedHeader(true);
      setIsMenuVisibleWithCollapsedHeader(false);
    } else if (!isHeaderVisible && isScrollingUp) {
      setIsMenuVisibleWithCollapsedHeader(true);
    } else {
      setIsNetworkNavVisible(true);
      setIsCollapsedHeader(false);
    }
  };

  useEffect(() => {
    if (appSubHeaderRef?.current && appHeaderNavRef?.current) {
      setNetworkHeaderHeight(appSubHeaderRef.current.offsetHeight);
      setNetworkNavHeight(appHeaderNavRef.current.offsetHeight);
    } else if (isPO) {
      // If we are in PO. We have to get the height of the header from the DOM
      // Header in PO is currently in haml.
      const header = document.querySelector('header.publicoffice-header');

      setNetworkHeaderHeight((header as HTMLElement)?.offsetHeight || 0);
    }
  }, [appSubHeaderRef, appHeaderNavRef]);

  useEffect(() => {
    const mainGroupHeaderHeight = calculateMainGroupHeaderHeight();
    const isHeaderVisible = isMainGroupHeaderVisible(
      mainGroupHeaderHeight,
      currentScrollY,
    );

    updateGroupHeaderState(isHeaderVisible);
  }, [currentScrollY, isScrollingUp, networkHeaderHeight, networkNavHeight]);

  const displayManageButton = topic.can_edit && !isSmallBreakpoint;

  return (
    <ImmersiveMenu location={window.location} network={currentNetwork}>
      {({ isImmersiveMenu }) => (
        <>
          <Box
            data-testid="main-group-header"
            flexDirection="column"
            ref={mainGroupHeaderRef}
            width="100%"
            mt={{ _: 0, l: 4 }}
          >
            <GroupHeader
              JoinButtonComponent={JoinButton}
              MenuComponent={
                <Box mt={{ m: 4, xs: 2 }} position="relative">
                  <MenuScrollable topic={topic} />
                </Box>
              }
              collapsed={false}
              coverPictureUrl={topic.cover_picture_url}
              groupName={topic.name}
              logoUrl={topic.logo_url}
              members={members}
              {...(displayManageButton
                ? { onManageGroupClick: handleManageGroupClick }
                : undefined)}
              onMembersClick={() => setIsMembersModalOpen(true)}
              onSearch={
                isPO || flagUeSearchDeactivateGlobal ? undefined : handleSearch
              }
              socials={socials}
              totalMembers={totalMembers}
              threeDotsButtonData={threeDotsButtonData}
            />
          </Box>

          <AnimatedCollapsedContainer
            // Must be only the SubHeader height and not with the network nav height
            $networkHeaderHeight={isImmersiveMenu ? 0 : networkHeaderHeight}
            aria-hidden
            data-testid="collapsed-group-header"
            style={collapsedHeaderSpring}
            tabIndex={-1}
          >
            <GroupHeader
              JoinButtonComponent={JoinButton}
              MenuComponent={
                <AnimatedWrapperMenuScrollable style={menuSpring}>
                  <MenuScrollable topic={topic} />
                </AnimatedWrapperMenuScrollable>
              }
              collapsed
              coverPictureUrl={topic.cover_picture_url}
              groupName={topic.name}
              logoUrl={topic.logo_url}
              onSearch={
                isPO || flagUeSearchDeactivateGlobal ? undefined : handleSearch
              }
              socials={socials}
              threeDotsButtonData={threeDotsButtonData}
            />
          </AnimatedCollapsedContainer>

          {topic.can_edit && (
            <SidePreview
              headerVisible={false}
              isPortalOpened={isSidePreviewOpen}
              onClose={handleSidePreviewClose}
              url={Routes.backoffice_log_as_user_duallog_as_group_admin_path({
                network_id: currentNetwork.id,
                topic_id: topic.id,
              })}
            />
          )}
          {isMembersModalOpen ? (
            <UsersModal
              onCloseModal={() => setIsMembersModalOpen(false)}
              title={I18n.t('web.topics.followers.other')}
              routes={{
                fetchUsers: Routes.topics_topic_followings_path(topic.id),
              }}
              responseCallback={(response) => ({
                users: response.followings.map((following) => following.user),
                total: response.total,
              })}
              screenReadersCloseButtonText={s__(
                'TopicsAccessibility|Close the topic followers list window',
              )}
            />
          ) : null}
        </>
      )}
    </ImmersiveMenu>
  );
};

const AnimatedBox = animated(Box);

const AnimatedCollapsedContainer = styled(AnimatedBox as any)<{
  $networkHeaderHeight: number;
}>`
  flex-direction: column;
  position: fixed;
  top: ${({ $networkHeaderHeight }) => $networkHeaderHeight}px;
  left: 0;
  right: 0;
  z-index: ${zIndices.pinned};
`;

const AnimatedWrapperMenuScrollable = styled(AnimatedBox as any).attrs(() => ({
  mt: { m: 4, xs: 2 },
}))`
  position: relative;
`;
